import { useState } from 'react';
import { UnsavedChangesModal } from '@/core/UnsavedChangesModal.molecule';
import type { ReactElement } from 'react';

type UnsavedChangesGuardOptions = {
  /** translation key for the message explaining what will be discarded */
  messageKey: string;
};

type UnsavedChangesGuard = {
  /** tells the guard whether there are currently unsaved changes */
  setHasUnsavedChanges: (hasUnsavedChanges: boolean) => void;
  /** runs the action right away when nothing is unsaved; otherwise asks the user to discard their changes first */
  guard: (action: () => void) => void;
  /** the confirmation prompt, or null when it is not open; render it alongside the guarded content */
  unsavedChangesModal: ReactElement | null;
};

/**
 * Asks the user before an action (e.g. switching tabs) would throw away unsaved changes.
 */
export const useUnsavedChangesGuard = ({ messageKey }: UnsavedChangesGuardOptions): UnsavedChangesGuard => {
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [pendingAction, setPendingAction] = useState<() => void>();

  const guard = (action: () => void): void => {
    if (hasUnsavedChanges) {
      setPendingAction(() => action);
    } else {
      action();
    }
  };

  const unsavedChangesModal = pendingAction ? (
    <UnsavedChangesModal
      messageKey={messageKey}
      onCancel={() => setPendingAction(undefined)}
      onDiscard={() => {
        setPendingAction(undefined);
        setHasUnsavedChanges(false);
        pendingAction();
      }}
    />
  ) : null;

  return { setHasUnsavedChanges, guard, unsavedChangesModal };
};
